import {
  CheckCircle2,
  Clock,
  Quote,
  MessageSquare,
  DollarSign,
  CalendarDays,
  ArrowRight,
  ArrowLeft,
  RotateCcw,
  ShieldAlert,
  UserX,
  BarChart2,
  Layers,
  ClipboardCheck,
  ArrowRightLeft,
} from 'lucide-react'
import {
  TrendingDown,
  ShieldCheck,
  CalendarClock,
  Sparkles,
} from 'lucide-react'
import { CHALLENGES } from '@/data/modules'
import type { useProgress } from '@/hooks/useProgress'
import { StepWalkthrough } from '@/components/modules/StepWalkthrough'
import { MermaidDiagram } from '@/components/modules/MermaidDiagram'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import type { PageView } from '@/types'

const ICON_MAP: Record<string, React.ReactNode> = {
  TrendingDown: <TrendingDown size={22} />,
  ShieldCheck: <ShieldCheck size={22} />,
  CalendarClock: <CalendarClock size={22} />,
  ArrowRightLeft: <ArrowRightLeft size={22} />,
  Sparkles: <Sparkles size={22} />,
}

interface ModulePageProps {
  moduleId: string
  modules: import('@/types').Module[]
  progress: ReturnType<typeof useProgress>
  onNavigate: (view: PageView) => void
  darkMode: boolean
}

export function ModulePage({ moduleId, modules, progress, onNavigate, darkMode }: ModulePageProps) {
  const moduleIndex = modules.findIndex(m => m.id === moduleId)
  const module = modules[moduleIndex]

  if (!module) {
    return (
      <div className="flex flex-col items-center justify-center py-24 gap-4">
        <p className="text-[var(--text-secondary)]">Module not found.</p>
        <Button onClick={() => onNavigate({ page: 'dashboard' })}>Back to Dashboard</Button>
      </div>
    )
  }

  const isComplete = progress.isComplete(module.id)
  const prevModule = moduleIndex > 0 ? modules[moduleIndex - 1] : null
  const nextModule = moduleIndex < modules.length - 1 ? modules[moduleIndex + 1] : null

  return (
    <div className="space-y-8 animate-slide-up">
      {/* Module header */}
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-[var(--accent-blue-light)] flex items-center justify-center text-[var(--accent-blue)]">
          {ICON_MAP[module.icon]}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <Badge variant="blue">Method {module.number} of {modules.length}</Badge>
            {isComplete && (
              <Badge variant="green">
                <CheckCircle2 size={10} /> Complete
              </Badge>
            )}
            <span className="flex items-center gap-1 text-xs text-[var(--text-muted)]">
              <Clock size={12} />
              {module.estimatedMinutes} min read
            </span>
          </div>
          <h1 className="text-xl font-bold text-[var(--text-primary)] leading-tight">
            {module.title}
          </h1>
          <p className="text-sm text-[var(--text-secondary)] mt-1">{module.tagline}</p>
        </div>
      </div>

      {/* Overview */}
      <Card className="text-sm text-[var(--text-secondary)] leading-relaxed">
        <p className="font-semibold text-[var(--text-primary)] mb-2">Overview</p>
        {module.overview}
      </Card>

      {/* Challenges addressed */}
      {module.customerChallenges.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-[var(--text-secondary)] uppercase tracking-wide mb-3 flex items-center gap-2">
            <ShieldAlert size={14} />
            Customer challenges this method addresses
          </h2>
          <div className="space-y-2">
            {module.customerChallenges.map(idx => {
              const [title, detail] = CHALLENGES[idx].split(' — ')
              const challengeIcons = [UserX, BarChart2, Layers, ShieldAlert, ClipboardCheck, ArrowRightLeft]
              const ChallengeIcon = challengeIcons[idx]
              return (
                <div key={idx} className="flex items-start gap-2.5 text-sm">
                  <span className="flex-shrink-0 w-5 h-5 rounded-full bg-[var(--accent-blue-light)] flex items-center justify-center text-[var(--accent-blue)] mt-0.5">
                    <ChallengeIcon size={11} strokeWidth={2.5} />
                  </span>
                  <div>
                    <span className="font-medium text-[var(--text-primary)]">{title}</span>
                    {detail && (
                      <span className="text-[var(--text-muted)]"> — {detail}</span>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </section>
      )}

      {/* Step walkthrough */}
      <section>
        <h2 className="text-sm font-semibold text-[var(--text-secondary)] uppercase tracking-wide mb-3">
          Step-by-step walkthrough
        </h2>
        <StepWalkthrough steps={module.steps} />
      </section>

      {/* Sequence diagram */}
      <section>
        <h2 className="text-sm font-semibold text-[var(--text-secondary)] uppercase tracking-wide mb-3">
          How it flows
        </h2>
        <Card padding={false} className="p-4 overflow-hidden">
          <MermaidDiagram diagram={module.mermaidDiagram} dark={darkMode} />
        </Card>
      </section>

      {/* Real-world example */}
      {module.realWorldExample && (
        <Card className="border-l-4 border-l-[var(--accent-orange)] rounded-l-none">
          <div className="flex items-start gap-3">
            <Quote size={18} className="flex-shrink-0 text-[var(--accent-orange)] mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-[var(--accent-orange)] uppercase tracking-wide mb-2">
                Real-world example
              </p>
              <p className="text-sm font-medium text-[var(--text-primary)] mb-1">
                {module.realWorldExample.scenario}
              </p>
              <p className="text-sm text-[var(--text-secondary)]">
                {module.realWorldExample.outcome}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Additional services */}
      {module.additionalServices && module.additionalServices.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-[var(--text-secondary)] uppercase tracking-wide mb-3">
            Additional services from this method
          </h2>
          <Card>
            <ul className="space-y-2.5">
              {module.additionalServices.map((svc, i) => {
                const [title, detail] = svc.split(' — ')
                return (
                  <li key={i} className="flex items-start gap-2.5 text-sm">
                    <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-[var(--accent-blue)] mt-2" />
                    <div>
                      <span className="font-medium text-[var(--text-primary)]">{title}</span>
                      {detail && (
                        <span className="text-[var(--text-muted)]"> — {detail}</span>
                      )}
                    </div>
                  </li>
                )
              })}
            </ul>
          </Card>
        </section>
      )}

      {/* Key talking points */}
      <section>
        <h2 className="text-sm font-semibold text-[var(--text-secondary)] uppercase tracking-wide mb-3 flex items-center gap-2">
          <MessageSquare size={14} />
          Key talking points
        </h2>
        <div className="space-y-2">
          {module.keyTalkingPoints.map((point, i) => (
            <div
              key={i}
              className="flex items-start gap-3 p-3 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border)]"
            >
              <span className="flex-shrink-0 text-[var(--accent-blue)] font-bold text-sm mt-0.5">
                "
              </span>
              <p className="text-sm text-[var(--text-secondary)] italic leading-relaxed">
                {point.replace(/^"|"$/g, '')}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing / cadence info */}
      {(module.pricingNote || module.cadence || module.heroAngle) && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {module.pricingNote && (
            <Card className="flex items-start gap-3">
              <DollarSign size={18} className="flex-shrink-0 text-emerald-500 mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wide mb-1">Pricing</p>
                <p className="text-sm text-[var(--text-primary)]">{module.pricingNote}</p>
              </div>
            </Card>
          )}
          {module.cadence && (
            <Card className="flex items-start gap-3">
              <CalendarDays size={18} className="flex-shrink-0 text-[var(--accent-blue)] mt-0.5" />
              <div>
                <p className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wide mb-1">Cadence</p>
                <p className="text-sm text-[var(--text-primary)]">{module.cadence}</p>
              </div>
            </Card>
          )}
          {module.heroAngle && (
            <Card className="sm:col-span-2 border-l-4 border-l-[var(--accent-blue)] rounded-l-none bg-[var(--accent-blue-light)]">
              <p className="text-xs font-semibold text-[var(--accent-blue)] uppercase tracking-wide mb-2">The "hero" angle</p>
              <p className="text-sm text-[var(--text-primary)] leading-relaxed">{module.heroAngle}</p>
            </Card>
          )}
        </div>
      )}

      {/* Complete / navigation */}
      <div className="pt-2 pb-6 border-t border-[var(--border)] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex gap-2">
          {prevModule && (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onNavigate({ page: 'module', moduleId: prevModule.id })}
            >
              <ArrowLeft size={14} />
              Method {prevModule.number}
            </Button>
          )}
        </div>

        <div className="flex gap-2 flex-wrap">
          {isComplete ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => progress.markIncomplete(module.id)}
              className="text-[var(--text-muted)]"
            >
              <RotateCcw size={14} />
              Mark incomplete
            </Button>
          ) : (
            <Button
              variant="primary"
              size="md"
              onClick={() => progress.markComplete(module.id)}
            >
              <CheckCircle2 size={16} />
              Mark as complete
            </Button>
          )}

          {nextModule && (
            <Button
              variant={isComplete ? 'primary' : 'secondary'}
              size={isComplete ? 'md' : 'sm'}
              onClick={() => onNavigate({ page: 'module', moduleId: nextModule.id })}
            >
              Next: Method {nextModule.number}
              <ArrowRight size={14} />
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
